import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import CASES from "@/data/caseStudies";

/**
 * Compact "latest use cases" strip — used at the bottom of the case study
 * detail and feed pages. Shows a horizontal row that scrolls on overflow.
 */
// Reorder so no two consecutive cards share the same vendor.
const reorderByVendor = (items) => {
  const groups = new Map();
  items.forEach((it) => {
    if (!groups.has(it.vendor)) groups.set(it.vendor, []);
    groups.get(it.vendor).push(it);
  });
  const result = [];
  let prev = null;
  while (result.length < items.length) {
    const candidates = [...groups.entries()]
      .filter(([v, arr]) => arr.length > 0 && v !== prev)
      .sort((a, b) => b[1].length - a[1].length);
    const pick =
      candidates[0] || [...groups.entries()].find(([, arr]) => arr.length > 0);
    if (!pick) break;
    const [vendor, arr] = pick;
    result.push(arr.shift());
    prev = vendor;
  }
  return result;
};

export default function LatestScroll({ excludeId, industryKey, title = "Latest use cases", limit = 8 }) {
  let list = [...CASES];
  if (industryKey) list = list.filter((c) => c.industryKey === industryKey);
  if (excludeId) list = list.filter((c) => c.id !== excludeId);
  list = reorderByVendor(list).slice(0, limit);
  if (list.length === 0) return null;

  return (
    <section
      data-testid="latest-scroll"
      className="relative z-10 border-t border-zinc-200 bg-white/60 py-12 sm:py-14"
    >
      <div className="mx-auto flex w-full max-w-6xl items-end justify-between gap-3 px-6 sm:px-10 lg:px-16">
        <div>
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-600">
            // {title.toLowerCase()}
          </p>
          <h3 className="mt-2 font-display text-2xl font-extrabold leading-tight tracking-tight text-zinc-900 sm:text-3xl">
            {title}
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
          <BadgeCheck className="h-3 w-3 text-emerald-600" />
          Verified
        </span>
      </div>

      <div className="mt-6 overflow-x-auto">
        <div className="mx-auto flex w-max min-w-full gap-4 px-6 sm:px-10 lg:px-16">
          {list.map((c) => (
            <Link
              key={c.id}
              to={`/case-study/${c.id}`}
              data-testid={`latest-${c.id}`}
              className="group flex w-[280px] shrink-0 flex-col gap-3 border border-zinc-200 bg-white p-4 no-underline transition-colors hover:border-emerald-500"
            >
              <div className="flex items-center justify-between gap-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                <span>{c.industry}</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-zinc-400 transition-colors group-hover:text-emerald-600" />
              </div>
              <p className="font-display text-lg font-bold leading-tight text-zinc-900 group-hover:text-emerald-700">
                {c.customer}
              </p>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                {c.vendor}
              </p>
              <p className="mt-auto font-display text-2xl font-black text-emerald-700">{c.metric}</p>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                {c.metricLabel}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
